import React, { useEffect, useState } from 'react';
import tipoPersona from "../../../../mocks/tipoPersona.json";
import EstadosCiviles from "../../../../mocks/estadosCiviles.json";
import Sexos from "../../../../mocks/sexos.json";
import { ciudadesSelect, estadoCivilSelect, BeneficioSiiSelect } from '../../models/participantes';
import { useParticipantes } from '../../hooks/useParticipantes';
import { Deudor } from '../../models/deudor';
import * as Yup from "yup";
import validaRut from '../../../../utils/validaRut';
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from 'react-hook-form';
import { enumTipoParticipante, enumTipoPersona, TipoFuncionalidad } from '../../../../utils/enums';
import ListaSeguros from './listaSeguros';
import { useToast } from '../../../../hooks/useToast';
import { useDeudor } from '../../hooks/useDeudor';
import Validaciones from '../../../../utils/validaciones';
import ConfirmationModal from '../../../../components/ui/materialui/confirmationModal';
import MButton from '../../../../components/inputs/yup/materialui/mButton';
import EditIcon from '@mui/icons-material/Edit';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import CloseIcon from '@mui/icons-material/Close';
import { Card, CardContent, Divider, Grid2, Typography } from '@mui/material';
import InputText from '../../../../components/inputs/yup/materialui/inputText';
import InputSelect from '../../../../components/inputs/yup/materialui/inputSelect';
import InputNumber from '../../../../components/inputs/yup/materialui/inputNumber';
import InputRut from '../../../../components/inputs/yup/materialui/inputRut';
import InputFecha from '../../../../components/inputs/yup/materialui/inputFecha';
import { useUserRolesContext } from '../../../../context/userRolesContext';
import { useDetalleMutuoPermissions } from '../../hooks/useDetalleMutuoPermissions';
import { DetalleMutuoPermissionGuard } from '../../../../components/auth/DetalleMutuoPermissionGuard';

interface IProps {
    dataDeudor: Deudor | undefined;
    ciudadesSelect: ciudadesSelect[];
    estadoCivilSelect: estadoCivilSelect[];
    beneficioSiiSelect: BeneficioSiiSelect[];
    idMutuo: number;
    setShowParticipantes: React.Dispatch<React.SetStateAction<boolean>>;
    renderizar: () => void;
    volver: () => void;
    isComplementario: boolean;
    esMutuoNormalyConComplementario: boolean;
    isDesactivado?: boolean
    esVigente?: boolean;
}

const schemaDeudor = Yup.object<Deudor>().shape({
    tipodeudor: Yup.mixed<enumTipoPersona>().oneOf(Object.values(enumTipoPersona)).required(),
    rut: Yup.string()
        .default('')
        .nonNullable()
        .test('valida-rut', 'RUT inválido', function (value) {
            return validaRut.rutFormat(value);
        })
        .required(),

    apellidopaterno: Yup.string()
        .required("Campo requerido").max(50, "Máximo 50 caractéres")
        .test('valida-apellido-paterno', 'Caractéres inválidos', function (value) {
            return Validaciones.soloLetras(value ?? '')
        }),

    apellidomaterno: Yup.string()
        .required("Campo requerido")
        .max(50, "Máximo 50 caractéres")
        .test('valida-apellido-materno', 'Caractéres inválidos', function (value) {
            return Validaciones.soloLetras(value ?? '')
        }),

    nombres: Yup.string()
        .required("Campo requerido")
        .max(50, "Máximo 50 caractéres")
        .test('valida-nombre', 'Caractéres inválidos', function (value) {
            return Validaciones.soloLetras(value ?? '')
        }),

    fechanacimiento: Yup.date().nullable().test('mayor-edad', 'El deudor debe ser mayor de edad', function (value) {
        return Validaciones.esMayor18Anios(value ?? null)
    })
        .required("Campo requerido"),

    sexo: Yup.string()
        .required("Campo requerido"),

    profesion: Yup.string().nullable()
        .transform((value) => value === null ? '' : value)
        .max(50, "Máximo 50 caractéres")
        .test('valida-profesion', 'Caractéres inválidos', function (value) {
            return Validaciones.soloLetras(value ?? '')
        }),

    estadocivil: Yup.string()
        .required("Campo requerido"),

    nacionalidad: Yup.string()
        .required("Campo requerido")
        .max(30, "Máximo 30 caractéres")
        .test('valida-nacionalidad', 'Caractéres inválidos', function (value) {
            return Validaciones.soloLetras(value ?? '')
        }),

    telefonoparticular: Yup.number()
        .transform((value) => Number.isNaN(value) ? null : value)
        .notRequired()
        .max(999999999, "Máximo 9 digitos"),

    telefonocelular: Yup.number()
        .transform((value) => Number.isNaN(value) ? null : value)
        .notRequired()
        .max(999999999, "Máximo 9 digitos"),

    telefonooficina: Yup.number()
        .transform((value) => Number.isNaN(value) ? null : value)
        .notRequired()
        .max(999999999, "Máximo 9 digitos"),

    email: Yup.string().required("Campo requerido")
        .max(50, "Máximo 50 caractéres")
        .test('valida-email', 'Email inválido', function (value) {
            return Validaciones.esValidoEmail(value ?? '');
        }),

    direccion: Yup.string()
        .required("Campo requerido")
        .max(100, "Máximo 100 caractéres")
        .test('valida-direccion', 'Caractéres inválidos', function (value) {
            return Validaciones.isDireccion(value ?? '')
        }),

    ciudadId: Yup.string()
        .required("Campo requerido"),

    comunaId: Yup.string()
        .required("Campo requerido"),

    direccionCorrespondencia: Yup.string()
        .required("Campo requerido")
        .max(100, "Máximo 100 caractéres")
        .test('valida-direccion-correspondencia', 'Caractéres inválidos', function (value) {
            return Validaciones.isDireccion(value ?? '')
        }),

    ciudadCorrespondenciaId: Yup.string().required("Campo requerido"),

    comunaCorrespondenciaId: Yup.string().required("Campo requerido"),

    porcentajedominio: Yup.number().required("Campo requerido")
        .transform((value) => Number.isNaN(value) ? null : value)
        .min(0, "Valor debe ser mayor o igual a 0")
        .test({
            name: 'valida-max-100',
            exclusive: false,
            params: {},
            message: 'Valor no puede superar el 100%',
            test: (value, context) => value === undefined ||
                value === null ||
                value <= 100
        }),

    beneficioSiiAsignado: Yup.string().nullable(),
    rolPropiedadBeneficioSii: Yup.string().nullable(),

    porcentajeDividendoRenta: Yup.number().required("Campo requerido")
        .transform((value) => Number.isNaN(value) ? null : value)
        .min(0, "Valor debe ser mayor o igual a 0")
        .test({
            name: 'valida-max-100',
            exclusive: false,
            params: {},
            message: 'Valor no puede superar el 100%',
            test: (value) => value === undefined ||
                value === null ||
                value <= 100
        }),

});



const DeudorForm: React.FC<IProps> = (props) => {

    const { comunaPorId, comunasSelect, comunasCorrespondecia, setComunasSelect, setComunasCorrespondecia } = useParticipantes();
    const { actualizarDeudor } = useDeudor();

    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [modalConfirmacion, setModalConfirmacion] = useState({
        show: false,
        title: '',
        message: '',
        metodo: () => {
        },
    });

    const {
        formState: { errors, isDirty, isValid },
        handleSubmit,
        control,
        setValue,
        watch,
        reset,
        trigger
    } = useForm<Deudor>({
        defaultValues: props.dataDeudor,
        mode: "onChange",
        reValidateMode: "onChange",
        resolver: yupResolver(schemaDeudor),
    })

    const { addToast } = useToast();
    const dataFormDeudor = watch();
    const { permisoCarteraInfo, permisoCarteraContacto } = useDetalleMutuoPermissions(props.esVigente);

    useEffect(() => {
        if (props.dataDeudor?.ciudadId)
            comunaPorId(props.dataDeudor?.ciudadId);

        if (props.dataDeudor?.ciudadCorrespondenciaId)
            comunaPorId(props.dataDeudor?.ciudadCorrespondenciaId, true);

        if (props.dataDeudor) {
            reset(props.dataDeudor);
        }

    }, [props.dataDeudor, reset])

    const handleCiudadComuna = async (id: string | null, esCorrespondencia: boolean) => {

        if (esCorrespondencia) {
            setValue('comunaCorrespondenciaId', '');
            setComunasCorrespondecia([]);
        } else {
            setValue('comunaId', '');
            setComunasSelect([]);
        }

        trigger("comunaId");
        trigger("comunaCorrespondenciaId");

        if (id) {
            await comunaPorId(id, esCorrespondencia);
        }
    }

    const handleCancelarEdicionClick = () => {

        if (isDirty) {
            setModalConfirmacion({
                show: true,
                title: "Hay cambios sin guardar",
                message: "¿Desea abandonar la edición?",
                metodo: () => {
                    setIsEditing(false);
                    reset();
                },
            });
        }
        else {
            setIsEditing(false);
            reset();
        }

    }

    const onSubmit = (data: Deudor) => {

        setIsSubmitting(true);

        actualizarDeudor(data)
            .then(() => {
                addToast({
                    mostrar: true,
                    titulo: 'Confirmación',
                    tipo: 'Success',
                    mensaje: 'Datos modificados correctamente'
                });
                setIsEditing(false);
                setIsSubmitting(false);
                reset(dataFormDeudor);
                props.renderizar();
            })
            .catch((error: Error) => {
                addToast({
                    mostrar: true,
                    titulo: 'Error',
                    tipo: 'Danger',
                    mensaje: error?.message ?? 'Ha ocurrido un error'
                });
                setIsSubmitting(false);
            });
    }

    const isReadonlyWithPermissionContacto = () => {
        // Si NO es vigente (Bandeja), NO valida permisos
        if (!props.esVigente) return !isEditing;

        // Si ES vigente (Cartera), valida permiso Cartera_editar_contacto
        return !isEditing || !permisoCarteraContacto;
    }
    const isReadonlyWithPermission = () => {
        // Si NO es vigente (Bandeja), NO valida permisos
        if (!props.esVigente) return !isEditing;

        // Si ES vigente (Cartera), valida permiso
        return !isEditing || !permisoCarteraInfo;
    }
    return (
        <>

            <ConfirmationModal
                onHide={() =>
                    setModalConfirmacion({
                        ...modalConfirmacion,
                        show: false,
                    })
                }
                onConfirm={modalConfirmacion.metodo}
                show={modalConfirmacion.show}
                title={modalConfirmacion.title}
                message={modalConfirmacion.message}
            />

            <form onSubmit={handleSubmit(onSubmit)} autoComplete='off'>

                {!isEditing && <>
                    <Grid2 container alignItems="center" spacing={2}>
                        <Grid2 size={2}>
                            <h3>Deudor</h3>
                        </Grid2>
                        <DetalleMutuoPermissionGuard
                            esVigente={props.esVigente}
                            permissions={[TipoFuncionalidad.Bandeja_editar, TipoFuncionalidad.Cartera_editar_info, TipoFuncionalidad.Cartera_editar_contacto]}>

                            <Grid2 container spacing={1} size={10} sx={{ textAlign: "right", justifyContent: 'flex-end' }}>
                                {!props.isComplementario && (
                                    <MButton
                                        variant="contained"
                                        onClick={() => { setIsEditing(true); trigger(); }}
                                        disabled={props.isDesactivado}
                                        startIcon={<EditIcon />}
                                    >
                                        Editar
                                    </MButton>
                                )}

                                <MButton
                                    variant="contained"
                                    onClick={() => { props.setShowParticipantes(true); setIsEditing(false); props.volver(); }}
                                >
                                    Listado participantes
                                </MButton>
                            </Grid2>
                        </DetalleMutuoPermissionGuard>
                    </Grid2>

                </>
                }

                {isEditing && <>

                    <Grid2 container alignItems="center" spacing={2}>
                        <Grid2 size={2}>
                            <h3>Deudor</h3>
                        </Grid2>
                        <Grid2 container spacing={1} size={10} sx={{ textAlign: "right", justifyContent: 'flex-end' }}>
                            <MButton
                                variant='contained'
                                color='primary'
                                disabled={!(isDirty && isValid) || isSubmitting}
                                onClick={handleSubmit(onSubmit)}
                                startIcon={<SaveAltIcon />}
                            >
                                Guardar
                            </MButton>

                            <MButton
                                variant='contained'
                                color={'inherit'}
                                onClick={() => { handleCancelarEdicionClick(); }}
                                startIcon={<CloseIcon />}
                            >
                                Cancelar
                            </MButton>
                        </Grid2>
                    </Grid2>
                </>
                }

                <Divider sx={{ my: 2 }} />

                <Grid2 sx={{ mt: 2, mb: 3 }}>

                    <Card raised sx={{ mb: 2 }}>
                        <CardContent>
                            <Typography className='card-title' variant="h6" gutterBottom>
                                Datos Personales
                            </Typography>

                            <Grid2 container spacing={2} sx={{ mt: 2 }}>
                                <Grid2 size={3}>
                                    <InputSelect
                                        label="Tipo persona"
                                        name="tipodeudor"
                                        control={control}
                                        options={tipoPersona}
                                        error={errors.tipodeudor}
                                        readonly
                                    />
                                </Grid2>

                                <Grid2 size={3}>
                                    <InputRut
                                        label="RUT"
                                        name="rut"
                                        control={control}
                                        error={errors.rut}
                                        readonly
                                    />
                                </Grid2>

                                <Grid2 size={3}>
                                    <InputText
                                        label="Apellido paterno"
                                        name="apellidopaterno"
                                        control={control}
                                        error={errors.apellidopaterno}
                                        readonly={isReadonlyWithPermission()}
                                    />
                                </Grid2>

                                <Grid2 size={3}>
                                    <InputText
                                        label="Apellido materno"
                                        name="apellidomaterno"
                                        control={control}
                                        error={errors.apellidomaterno}
                                        readonly={isReadonlyWithPermission()}
                                    />
                                </Grid2>

                                <Grid2 size={3}>
                                    <InputText
                                        label="Nombres"
                                        name="nombres"
                                        control={control}
                                        error={errors.nombres}
                                        readonly={isReadonlyWithPermission()}
                                    />
                                </Grid2>

                                <Grid2 size={3}>

                                    <InputFecha
                                        label='Fecha nacimiento'
                                        name="fechanacimiento"
                                        control={control}
                                        fecha={dataFormDeudor?.fechanacimiento ?? undefined}
                                        error={errors.fechanacimiento}
                                        esReadOnly={isReadonlyWithPermission()}
                                    />

                                </Grid2>

                                <Grid2 size={3}>
                                    <InputSelect
                                        label="Sexo"
                                        name="sexo"
                                        control={control}
                                        options={Sexos}
                                        error={errors.sexo}
                                        disabled={isReadonlyWithPermission()}
                                    />
                                </Grid2>

                                <Grid2 size={3}>
                                    <InputText
                                        label="Profesión o actividad"
                                        name="profesion"
                                        control={control}
                                        error={errors.profesion}
                                        readonly={isReadonlyWithPermission()}
                                    />
                                </Grid2>

                                <Grid2 size={3}>
                                    <InputSelect
                                        label="Estado civil y régimen matrimonial"
                                        name="estadocivil"
                                        control={control}
                                        options={EstadosCiviles}
                                        error={errors.estadocivil}
                                        disabled={isReadonlyWithPermission()}
                                    />
                                </Grid2>

                                <Grid2 size={3}>
                                    <InputText
                                        label="Nacionalidad"
                                        name="nacionalidad"
                                        control={control}
                                        error={errors.nacionalidad}
                                        readonly={isReadonlyWithPermission()}
                                    />
                                </Grid2>
                                <Grid2 size={3}>
                                    <InputNumber
                                        label="Porcentaje dividendo renta"
                                        name="porcentajeDividendoRenta"
                                        control={control}
                                        readonly 
                                        decimales={2}
                                        minValue={0}
                                        maxValue={999999999}
                                        error={errors.porcentajeDividendoRenta}
                                        suffix='%'
                                    />
                                </Grid2>
                            </Grid2>
                        </CardContent>
                    </Card>

                    <Card raised sx={{ mb: 2 }}>
                        <CardContent>
                            <Typography className='card-title' variant="h6">Contacto</Typography>
                            <Grid2 container spacing={2} mt={2}>
                                <Grid2 size={3}>
                                    <InputNumber
                                        label="Teléfono particular"
                                        name="telefonoparticular"
                                        control={control}
                                        readonly={isReadonlyWithPermissionContacto()}
                                        decimales={0}
                                        minValue={0}
                                        maxValue={999999999}
                                        error={errors.telefonoparticular}
                                        prefix='+56'
                                    />
                                </Grid2>
                                <Grid2 size={3}>
                                    <InputNumber
                                        label="Teléfono celular"
                                        name="telefonocelular"
                                        control={control}
                                        readonly={isReadonlyWithPermissionContacto()}
                                        minValue={0}
                                        maxValue={999999999}
                                        error={errors.telefonocelular}
                                        prefix='+569'
                                    />
                                </Grid2>
                                <Grid2 size={3}>
                                    <InputNumber
                                        label="Teléfono oficina"
                                        name="telefonooficina"
                                        control={control}
                                        readonly={isReadonlyWithPermissionContacto()}
                                        minValue={0}
                                        maxValue={999999999}
                                        error={errors.telefonooficina}
                                        prefix='+569'
                                    />
                                </Grid2>
                                <Grid2 size={3}>
                                    <InputText
                                        label="Email"
                                        name="email"
                                        control={control}
                                        error={errors.email}
                                        readonly={isReadonlyWithPermissionContacto()}
                                    />
                                </Grid2>
                            </Grid2>
                        </CardContent>
                    </Card>

                    <Card raised sx={{ mb: 2 }}>
                        <CardContent>
                            <Typography className='card-title' variant="h6">Dirección particular</Typography>
                            <Grid2 container spacing={2} mt={2}>
                                <Grid2 size={3}>
                                    <InputText
                                        label="Calle"
                                        name="direccion"
                                        control={control}
                                        error={errors.direccion}
                                        readonly={isReadonlyWithPermission()}
                                    />
                                </Grid2>
                                <Grid2 size={3}>
                                    <InputSelect
                                        label="Ciudad"
                                        name="ciudadId"
                                        control={control}
                                        options={props.ciudadesSelect}
                                        error={errors.ciudadId}
                                        disabled={isReadonlyWithPermission()}
                                        onChange={(value) => handleCiudadComuna(value ?? "", false)}
                                    />
                                </Grid2>
                                <Grid2 size={3}>
                                    <InputSelect
                                        label="Comuna"
                                        name="comunaId"
                                        control={control}
                                        options={comunasSelect}
                                        error={errors.comunaId}
                                        disabled={isReadonlyWithPermission()}
                                    />
                                </Grid2>
                            </Grid2>

                            <Typography className='card-title' variant="h6" mt={2}>Dirección envío correspondencia</Typography>
                            <Grid2 container spacing={2} mt={2}>
                                <Grid2 size={3}>
                                    <InputText
                                        label="Calle"
                                        name="direccionCorrespondencia"
                                        control={control}
                                        error={errors.direccionCorrespondencia}
                                        readonly={isReadonlyWithPermission()}
                                    />
                                </Grid2>
                                <Grid2 size={3}>
                                    <InputSelect
                                        label="Ciudad"
                                        name="ciudadCorrespondenciaId"
                                        control={control}
                                        options={props.ciudadesSelect}
                                        error={errors.ciudadCorrespondenciaId}
                                        onChange={(value) => handleCiudadComuna(value, true)}
                                        disabled={isReadonlyWithPermission()}
                                    />
                                </Grid2>
                                <Grid2 size={3}>
                                    <InputSelect
                                        label="Comuna"
                                        name="comunaCorrespondenciaId"
                                        control={control}
                                        options={comunasCorrespondecia}
                                        error={errors.comunaCorrespondenciaId}
                                        disabled={isReadonlyWithPermission()}
                                    />
                                </Grid2>
                            </Grid2>
                        </CardContent>
                    </Card>

                    <Card raised sx={{ mb: 2 }}>
                        <CardContent>
                            <Typography className='card-title' variant="h6" gutterBottom>
                                Datos deuda
                            </Typography>

                            <Grid2 container spacing={2} sx={{ mt: 2 }}>
                                {/* Porcentaje dominio */}
                                <Grid2 size={{ xs: 12, md: 3 }}>
                                    <InputNumber
                                        label="Porcentaje dominio"
                                        name="porcentajedominio"
                                        control={control}
                                        readonly={isReadonlyWithPermission()}
                                        minValue={0}
                                        maxValue={100}
                                        error={errors.porcentajedominio}
                                        suffix='%'
                                    />
                                </Grid2>

                                {/* Beneficio tributario */}
                                <Grid2 size={{ xs: 12, md: 3 }}>
                                    <InputSelect
                                        label="Beneficio tributario"
                                        name="beneficioSiiAsignado"
                                        control={control}
                                        options={props.beneficioSiiSelect}
                                        error={errors.beneficioSiiAsignado}
                                        clearable={true}
                                        disabled={props.isComplementario || isReadonlyWithPermission() || props.dataDeudor?.existeCodeudorBeneficiarioSii!}
                                    />
                                </Grid2>

                                {/* Rol */}
                                <Grid2 size={{ xs: 12, md: 3 }}>
                                    <InputText
                                        label="Rol"
                                        name="rolPropiedadBeneficioSii"
                                        control={control}
                                        error={errors.rolPropiedadBeneficioSii}
                                        readonly={isReadonlyWithPermission()}
                                    />
                                </Grid2>
                            </Grid2>
                        </CardContent>
                    </Card>

                    <Card raised sx={{ mb: 2 }}>
                        <CardContent className="card-body">
                            <Typography className='card-title' variant="h6" gutterBottom>
                                Seguros
                            </Typography>
                            <ListaSeguros
                                idMutuo={props.idMutuo}
                                rut={''}
                                tipoParticipante={enumTipoParticipante.Deudor}
                                porcentajedeudaseguro={0}
                                tipoDeudor={dataFormDeudor.tipodeudor}
                                isDesactivado={props.isDesactivado}
                                esVigente={props.esVigente!}
                            ></ListaSeguros>

                        </CardContent>
                    </Card>

                </Grid2>
            </form>

        </>
    );
};

export default DeudorForm;



